import { ReactNode, useId } from 'react'
import { ChevronDown, Search, SlidersHorizontal } from 'lucide-react'
import { BOX_TRUCK_EQUIPMENT_LABELS, type BoxTruckDriverLastLocation } from '@numeo/types-core'
import { Button, SearchField } from '@/components/ds2'
import { CollapsibleRegion } from '@/components/common/CollapsibleRegion'
import { DateRangePicker } from 'features/load-hub/components/filters/DateRangePicker'
import { FilterInput } from 'features/load-hub/components/filters/FilterInput'
import { SEARCH_FILTER_LIMITS } from 'utils/search-filter-validation'
import { LocationData } from 'features/load-hub/types/filters'
import { BoxTruckEquipmentSelect } from './BoxTruckEquipmentSelect'
import { LanePicker } from './LanePicker'
import { SearchForDriverSelect } from './SearchForDriverSelect'
import { BoxTruckVerification } from '../services/boxTruckLoadsApi'
import { BoxTruckFiltersToolbar } from './BoxTruckFiltersToolbar'
import { useBoxTruckFiltersCollapsed } from '../hooks/useBoxTruckFiltersCollapsed'

interface BoxTruckFiltersProps {
    /** Multi-select origin/destination chips (one city OR several states). */
    originOptions: LocationData[]
    destinationOptions: LocationData[]
    /** Radius (miles) around the picked origin/destination CITY; only meaningful for a single-city pick (coords). */
    originRadius: number | null
    destinationRadius: number | null
    startDate: Date | null
    endDate: Date | null
    onOriginChange: (value: LocationData[]) => void
    onDestinationChange: (value: LocationData[]) => void
    onOriginRadiusChange: (value: number | null) => void
    onDestinationRadiusChange: (value: number | null) => void
    onPickupChange: (start: Date | null, end: Date | null) => void
    /** Highlight the pickup field as a missing required value (mirrors Load Hub's required pickup). */
    pickupError?: boolean
    /** Origin-or-destination is required — anywhere-to-anywhere is blocked; flags the lane fields. */
    onSearch: () => void
    loading: boolean
    query: string
    onQueryChange: (value: string) => void
    /** Selected box-truck equipment labels — a server-side filter applied on the next Search. */
    equipmentLabels: string[]
    /** Absent when the ACTIVE board has no equipment filter to apply (My Biddings): the select is then not
     *  rendered at all, the same way the driver picker already self-suppresses on a missing handler. A
     *  rendered control that writes to a board nobody is looking at is worse than no control. */
    onEquipmentChange?: (values: string[]) => void
    /** Toggle one of the operator's drivers → prefill origin (their GPS) + equipment from the whole
     *  selection and run the search. */
    onDriverToggle?: (driver: BoxTruckVerification) => void
    /** The ACTIVE TAB's picked drivers (page-owned, keyed per tab) + their clear — controls the picker label. */
    selectedDrivers?: BoxTruckVerification[]
    /** A location the operator corrected from the picker — the page patches its persisted selection with it. */
    onDriverLocationSaved?: (userId: string, lastLocation: BoxTruckDriverLastLocation) => void
    onClearDrivers?: () => void
    /** Driver Bids tab: hide the lane-search row. Its driver/equipment refinements still apply; on mobile,
     *  equipment and the secondary board controls live under More filters. */
    hideLaneFilters?: boolean
    /** Reload the active source.
     *
     *  On a search tab there is no manual Refresh button: Search already re-fetches the active lane, so a
     *  second control did the same thing twice. The Driver Bids tab is the exception — it has no lane row and
     *  therefore no Search, so a manual reload is the only one it would otherwise have. */
    onRefresh: () => void
    /** Show the refresh control only when the active tab has searched (nothing to refresh otherwise). */
    canRefresh: boolean
    /** Client refinements (Broker Blacklist, trip miles, Advanced Filters) — rendered on the second row
     *  rather than a third one of their own. A slot, not props: the page owns their state and they have
     *  nothing to do with building a search request. */
    refinementsSlot?: ReactNode
    /** Pinned to the row's right edge, after the refresh cluster — the Search Vehicles action. */
    trailingSlot?: ReactNode
    /** Source visibility controls share this disclosure so collapsing the search area frees the entire
     *  vertical block rather than leaving a detached source row behind. */
    sourceSlot?: ReactNode
    /** Filters intentionally omitted from the compact lane summary are still active. */
    hasAdditionalFilters: boolean
    /** Detached browser tabs restore only their own compact-board preference. */
    isolatedWindow?: boolean
    /** Mobile-only disclosure for the secondary board controls. Desktop always shows them. */
    mobileMoreFiltersOpen: boolean
    onMobileMoreFiltersToggle: () => void
}

/** Whether the leg has anything to measure a radius FROM: a city pick, or one point per driver when several
 *  are selected. A state-only selection has no point, so the radius input stays disabled. */
const hasRadiusAnchor = (opts: LocationData[]): boolean => opts.some((o) => o.latitude != null && o.longitude != null)

/** Stable empty default for the optional picked-drivers prop — a fresh `[]` per render would re-run the
 *  picker's selection memo on every keystroke in the filter bar. */
const EMPTY_DRIVERS: BoxTruckVerification[] = []

const Field = ({ label, children, className = '' }: { label: string; children: React.ReactNode; className?: string }) => (
    <div className={`flex flex-col gap-1 ${className}`}>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">{label}</span>
        {children}
    </div>
)

/** Miles radius around a picked city — reuses Load Hub's numeric FilterInput (sanitized, clamped to the
 *  shared DHD ceiling) so it matches the adjacent origin/destination inputs. Disabled for a state-only pick,
 *  which has no coords: radius needs a point, so a state pick stays an exact state match. */

const RadiusInput = ({ value, onChange, enabled }: { value: number | null; onChange: (value: number | null) => void; enabled: boolean }) => (
    <div className="w-[84px] shrink-0 min-[900px]:w-[92px]">
        <FilterInput
            type="number"
            placeholder="Radius"
            width="100%"
            minValue={1}
            maxValue={SEARCH_FILTER_LIMITS.DHD_MAX}
            disabled={!enabled}
            value={value != null ? String(value) : ''}
            onChange={(v) => {
                const n = Math.round(Number(v))
                onChange(v === '' || !Number.isFinite(n) || n <= 0 ? null : n)
            }}
        />
    </div>
)

/**
 * Box Truck filter bar. Origin / destination / pickup / equipment reuse Load Hub's inputs and drive the
 * server search. Pickup is REQUIRED (defaults to today); origin/destination are optional —
 * anywhere-to-anywhere is allowed. Equipment is a multi-select of
 * the box-truck labels, applied server-side on Search (unified codes + Sylectus vehicle sizes); text search
 * stays a client filter over the fetched rows.
 *
 * `hideLaneFilters` (a pinned bids board) drops the lane row — origin/destination/pickup/Search mean nothing
 * for a bids list. The driver picker and equipment select survive it for Driver Bids, which narrows its
 * server query by both; My Biddings takes neither, and passes no handler so neither appears.
 */
export const BoxTruckFilters = ({
    originOptions,
    destinationOptions,
    originRadius,
    destinationRadius,
    startDate,
    endDate,
    onOriginChange,
    onDestinationChange,
    onOriginRadiusChange,
    onDestinationRadiusChange,
    onPickupChange,
    pickupError,
    onSearch,
    loading,
    query,
    onQueryChange,
    equipmentLabels,
    onEquipmentChange,
    onDriverToggle,
    selectedDrivers = EMPTY_DRIVERS,
    onDriverLocationSaved,
    onClearDrivers,
    hideLaneFilters = false,
    onRefresh,
    canRefresh,
    mobileMoreFiltersOpen,
    onMobileMoreFiltersToggle,
    refinementsSlot,
    trailingSlot,
    sourceSlot,
    hasAdditionalFilters,
    isolatedWindow = false,
}: BoxTruckFiltersProps) => {
    const filtersRegionId = useId()
    const { collapsed, setCollapsed } = useBoxTruckFiltersCollapsed(isolatedWindow)

    const handleCollapsedSearch = () => {
        if (!startDate || !endDate) setCollapsed(false)
        onSearch()
    }

    return (
        <>
            <CollapsibleRegion id={filtersRegionId} role="region" aria-label="Load search filters" open={!collapsed}>
                <div className="flex flex-col gap-2 px-3 py-2 min-[900px]:px-4">
                    {!hideLaneFilters && (
                        <div className="grid grid-cols-2 items-end gap-2 min-[900px]:flex min-[900px]:flex-wrap">
                            <Field label="Origin" className="col-span-2 min-w-0 min-[900px]:min-w-[220px] min-[900px]:flex-1">
                                <div className="flex items-center gap-2">
                                    <div className="min-w-0 flex-1">
                                        {/* Only the ORIGIN stacks points. `buildRequests` fans a lane out per origin point; the
                                            destination goes through one `toLocationPayload`, which searches a single
                                            circle — so a second destination chip would be shown and never searched. */}
                                        <LanePicker values={originOptions} onChange={onOriginChange} multiPoint />
                                    </div>
                                    <RadiusInput value={originRadius} onChange={onOriginRadiusChange} enabled={hasRadiusAnchor(originOptions)} />
                                </div>
                            </Field>
                            <Field label="Destination" className="col-span-2 min-w-0 min-[900px]:min-w-[220px] min-[900px]:flex-1">
                                <div className="flex items-center gap-2">
                                    <div className="min-w-0 flex-1">
                                        <LanePicker values={destinationOptions} onChange={onDestinationChange} />
                                    </div>
                                    <RadiusInput value={destinationRadius} onChange={onDestinationRadiusChange} enabled={hasRadiusAnchor(destinationOptions)} />
                                </div>
                            </Field>
                            {onEquipmentChange && (
                                <Field label="Equipment" className="min-w-0 min-[900px]:min-w-[180px]">
                                    <BoxTruckEquipmentSelect values={equipmentLabels} options={BOX_TRUCK_EQUIPMENT_LABELS} onChange={onEquipmentChange} />
                                </Field>
                            )}
                            <Field label="Pickup" className="col-span-2 min-w-0 min-[900px]:min-w-[180px]">
                                <div className="relative">
                                    <DateRangePicker startDate={startDate} endDate={endDate} onChange={onPickupChange} />
                                    {pickupError && (
                                        <span role="alert" className="absolute -bottom-3.5 left-0 ml-1 text-[10px] text-danger">
                                            Required
                                        </span>
                                    )}
                                </div>
                            </Field>
                            {/* Not a loading button. A search fans out one lane per picked driver and the board fills in
                        as each lands, so the progress belongs over the TABLE (where the rows appear), not on
                        the control the operator just used. Disabled while one is in flight so a second press
                        can't restart the fan-out mid-stream — the same shape Load Hub uses. */}
                            <Button
                                variant="primary"
                                size="md"
                                disabled={loading}
                                onClick={onSearch}
                                prefix={<Search size={16} />}
                                className="col-span-2 w-full min-[900px]:w-auto"
                            >
                                Search
                            </Button>
                        </div>
                    )}
                    <div className="flex flex-col gap-2 min-[900px]:flex-row min-[900px]:flex-wrap min-[900px]:items-center min-[900px]:gap-2">
                        <div
                            className={`${mobileMoreFiltersOpen ? 'block' : 'hidden'} order-3 w-full min-w-0 min-[900px]:order-none min-[900px]:block min-[900px]:w-[260px] min-[900px]:flex-none`}
                        >
                            <SearchField
                                value={query}
                                onChange={onQueryChange}
                                onSubmit={hideLaneFilters ? undefined : onSearch}
                                placeholder={
                                    hideLaneFilters
                                        ? 'Filter rows — order #, city, broker…'
                                        : 'Filter rows — order #, city, broker… or press Enter to find a Numeo ID'
                                }
                            />
                        </div>
                        {/* Equipment lives in the lane row above on a search tab; on Driver Bids (no lane row) it moves
                    down here next to the driver picker — the two filters that DO apply to a bids list. My
                    Biddings supports neither, so both arrive without a handler and neither is rendered. */}
                        {hideLaneFilters && onEquipmentChange && (
                            <div
                                className={`${mobileMoreFiltersOpen ? 'block' : 'hidden'} order-4 w-full min-[900px]:order-none min-[900px]:block min-[900px]:w-[180px]`}
                            >
                                <BoxTruckEquipmentSelect values={equipmentLabels} options={BOX_TRUCK_EQUIPMENT_LABELS} onChange={onEquipmentChange} />
                            </div>
                        )}
                        {/* The driver picker applies to BOTH surfaces: on a lane tab it prefills origin + equipment and
                    searches; on Driver Bids it narrows the bids to those drivers (server-side). */}
                        {onDriverToggle && (
                            <div className="order-1 w-full min-[900px]:order-none min-[900px]:w-[220px]">
                                <SearchForDriverSelect
                                    selected={selectedDrivers}
                                    onToggle={onDriverToggle}
                                    onLocationSaved={onDriverLocationSaved}
                                    onClearSelected={() => onClearDrivers?.()}
                                    requireAppAccount={hideLaneFilters}
                                />
                            </div>
                        )}
                        {refinementsSlot && (
                            <div
                                className={`${mobileMoreFiltersOpen ? 'flex' : 'hidden'} order-4 w-full flex-wrap items-center gap-2 min-[900px]:order-none min-[900px]:flex min-[900px]:w-auto`}
                            >
                                {refinementsSlot}
                            </div>
                        )}
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={onMobileMoreFiltersToggle}
                            prefix={<SlidersHorizontal size={15} />}
                            suffix={<ChevronDown size={15} className={`transition-transform ${mobileMoreFiltersOpen ? 'rotate-180' : ''}`} />}
                            aria-expanded={mobileMoreFiltersOpen}
                            className="order-2 w-full min-[900px]:hidden"
                        >
                            {mobileMoreFiltersOpen ? 'Less filters' : 'More filters'}
                        </Button>
                    </div>
                </div>
            </CollapsibleRegion>
            <BoxTruckFiltersToolbar
                canRefresh={canRefresh}
                collapsed={collapsed}
                destinationOptions={destinationOptions}
                endDate={endDate}
                equipmentLabels={equipmentLabels}
                filtersRegionId={filtersRegionId}
                hasAdditionalFilters={hasAdditionalFilters}
                hideLaneFilters={hideLaneFilters}
                loading={loading}
                mobileMoreFiltersOpen={mobileMoreFiltersOpen}
                onCollapse={() => setCollapsed(true)}
                onExpand={() => setCollapsed(false)}
                onRefresh={onRefresh}
                onSearch={handleCollapsedSearch}
                originOptions={originOptions}
                sourceSlot={sourceSlot}
                startDate={startDate}
                trailingSlot={trailingSlot}
            />
        </>
    )
}
